/**
 * SectorRegionInputModal Component
 *
 * Allows users to manually enter sector and region allocation data
 * for their investments based on fund factsheets/documentation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, Info, ExternalLink, PieChart, Globe } from 'lucide-react';
import {
  SECTORS,
  REGIONS,
  SECTOR_COLORS,
  REGION_COLORS,
  validateSectorBreakdown,
  validateRegionBreakdown
} from '../../utils/sectorData';

const SectorRegionInputModal = ({
  isOpen,
  onClose,
  onSave,
  investment,
  isDark = false
}) => {
  const [activeTab, setActiveTab] = useState('sectors');
  const [sectorData, setSectorData] = useState({});
  const [regionData, setRegionData] = useState({});
  const [error, setError] = useState(null);

  // Initialize with existing data
  useEffect(() => {
    if (isOpen && investment) {
      setSectorData(investment.sectorBreakdown || {});
      setRegionData(investment.regionBreakdown || {});
      setError(null);
    }
  }, [isOpen, investment]);

  // Calculate totals
  const sectorTotal = useMemo(() => {
    return Object.values(sectorData).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  }, [sectorData]);

  const regionTotal = useMemo(() => {
    return Object.values(regionData).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  }, [regionData]);

  const handleSectorChange = (sector, value) => {
    const numValue = value === '' ? '' : parseFloat(value) || 0;
    setSectorData(prev => ({
      ...prev,
      [sector]: numValue
    }));
  };

  const handleRegionChange = (region, value) => {
    const numValue = value === '' ? '' : parseFloat(value) || 0;
    setRegionData(prev => ({
      ...prev,
      [region]: numValue
    }));
  };

  const handleSave = () => {
    // Clean up empty values
    const cleanSectorData = {};
    Object.entries(sectorData).forEach(([key, val]) => {
      if (val && parseFloat(val) > 0) {
        cleanSectorData[key] = parseFloat(val);
      }
    });

    const cleanRegionData = {};
    Object.entries(regionData).forEach(([key, val]) => {
      if (val && parseFloat(val) > 0) {
        cleanRegionData[key] = parseFloat(val);
      }
    });

    // Validate if data was entered
    if (Object.keys(cleanSectorData).length > 0) {
      const sectorValidation = validateSectorBreakdown(cleanSectorData);
      if (!sectorValidation.valid) {
        setError(sectorValidation.message);
        setActiveTab('sectors');
        return;
      }
    }

    if (Object.keys(cleanRegionData).length > 0) {
      const regionValidation = validateRegionBreakdown(cleanRegionData);
      if (!regionValidation.valid) {
        setError(regionValidation.message);
        setActiveTab('regions');
        return;
      }
    }

    onSave({
      sectorBreakdown: Object.keys(cleanSectorData).length > 0 ? cleanSectorData : null,
      regionBreakdown: Object.keys(cleanRegionData).length > 0 ? cleanRegionData : null
    });
    onClose();
  };

  const clearAllSectors = () => {
    setSectorData({});
  };

  const clearAllRegions = () => {
    setRegionData({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
      >
        {/* Header */}
        <div className={`flex-shrink-0 flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className="text-xl font-bold">Sector & Region Allocation</h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {investment?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Info Banner */}
          <div className={`mx-6 mt-4 p-4 rounded-lg ${isDark ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-start gap-3">
            <Info className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                Where to find this information:
              </p>
              <ul className={`text-sm mt-2 space-y-1 ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
                <li>• <strong>Fund Factsheet</strong> - Usually has a "Portfolio Breakdown" or "Allocation" section</li>
                <li>• <strong>Fund Provider Website</strong> - Look for "Holdings" or "Composition" tabs</li>
                <li>• <strong>KIID/KID Document</strong> - Key Investor Information Document</li>
                <li>• <strong>Morningstar</strong> - Search for your fund and view the "Portfolio" tab</li>
              </ul>
              <p className={`text-xs mt-2 ${isDark ? 'text-blue-300/70' : 'text-blue-600'}`}>
                Totals can be less than 100% if the fund holds cash or other assets. Leave empty if you don't have this data.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-b mx-6 mt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('sectors')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'sectors'
                ? isDark
                  ? 'border-blue-400 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDark
                  ? 'border-transparent text-gray-400 hover:text-gray-200'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <PieChart className="w-4 h-4" />
            Sectors
            {sectorTotal > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                sectorTotal <= 100.5
                  ? 'bg-green-500/20 text-green-500'
                  : 'bg-red-500/20 text-red-500'
              }`}>
                {sectorTotal.toFixed(0)}%
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('regions')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'regions'
                ? isDark
                  ? 'border-blue-400 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDark
                  ? 'border-transparent text-gray-400 hover:text-gray-200'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4" />
            Regions
            {regionTotal > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                regionTotal <= 100.5
                  ? 'bg-green-500/20 text-green-500'
                  : 'bg-red-500/20 text-red-500'
              }`}>
                {regionTotal.toFixed(0)}%
              </span>
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500 flex items-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Content */}
          <div className="p-6">
          {activeTab === 'sectors' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-4">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Enter the percentage allocation for each sector (GICS Classification)
                </p>
                <button
                  onClick={clearAllSectors}
                  className={`text-xs ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SECTORS.map(sector => (
                  <div key={sector} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: SECTOR_COLORS[sector] }}
                    />
                    <label className={`flex-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {sector}
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={sectorData[sector] || ''}
                        onChange={(e) => handleSectorChange(sector, e.target.value)}
                        placeholder="0"
                        className={`w-20 px-2 py-1.5 text-right text-sm border rounded-l-lg ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <span className={`px-2 py-1.5 text-sm border-y border-r rounded-r-lg ${
                        isDark ? 'bg-gray-600 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600'
                      }`}>
                        %
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Total indicator */}
              <div className={`mt-4 pt-4 border-t flex justify-between items-center ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total:</span>
                <span className={`font-bold text-lg ${
                  sectorTotal === 0
                    ? isDark ? 'text-gray-500' : 'text-gray-400'
                    : sectorTotal > 100.5
                      ? 'text-red-500'
                      : 'text-green-500'
                }`}>
                  {sectorTotal.toFixed(1)}%
                  {sectorTotal > 0 && sectorTotal < 100 && (
                    <span className={`text-sm font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({(100 - sectorTotal).toFixed(1)}% cash/other)
                    </span>
                  )}
                  {sectorTotal > 100.5 && (
                    <span className="text-sm font-normal ml-2">(cannot exceed 100%)</span>
                  )}
                </span>
              </div>
            </div>
          )}

          {activeTab === 'regions' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-4">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Enter the percentage allocation for each geographic region
                </p>
                <button
                  onClick={clearAllRegions}
                  className={`text-xs ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {REGIONS.map(region => (
                  <div key={region} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: REGION_COLORS[region] }}
                    />
                    <label className={`flex-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {region}
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={regionData[region] || ''}
                        onChange={(e) => handleRegionChange(region, e.target.value)}
                        placeholder="0"
                        className={`w-20 px-2 py-1.5 text-right text-sm border rounded-l-lg ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <span className={`px-2 py-1.5 text-sm border-y border-r rounded-r-lg ${
                        isDark ? 'bg-gray-600 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600'
                      }`}>
                        %
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Total indicator */}
              <div className={`mt-4 pt-4 border-t flex justify-between items-center ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total:</span>
                <span className={`font-bold text-lg ${
                  regionTotal === 0
                    ? isDark ? 'text-gray-500' : 'text-gray-400'
                    : regionTotal > 100.5
                      ? 'text-red-500'
                      : 'text-green-500'
                }`}>
                  {regionTotal.toFixed(1)}%
                  {regionTotal > 0 && regionTotal < 100 && (
                    <span className={`text-sm font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({(100 - regionTotal).toFixed(1)}% cash/other)
                    </span>
                  )}
                  {regionTotal > 100.5 && (
                    <span className="text-sm font-normal ml-2">(cannot exceed 100%)</span>
                  )}
                </span>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex-shrink-0 flex items-center justify-between p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm ${
              isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2 text-sm font-medium"
          >
            <Save className="w-4 h-4" />
            Save Allocation Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectorRegionInputModal;
